import type { AdminUser, GroceryItem, GroceryList, UserSummary } from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      // Only set when there's an actual body — Fastify's JSON body parser
      // throws FST_ERR_CTP_EMPTY_JSON_BODY on an empty body sent with this
      // header, which broke every bodyless mutation (delete, logout,
      // clear-checked).
      ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore non-JSON error bodies
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  login: (username: string, password: string) =>
    request<{ user: UserSummary }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  me: () => request<{ user: UserSummary }>("/api/auth/me"),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  adminListUsers: () => request<{ users: AdminUser[] }>("/api/admin/users"),

  adminCreateUser: (username: string, password: string, role: "admin" | "user") =>
    request<{ user: AdminUser }>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({ username, password, role }),
    }),

  adminResetPassword: (userId: string, password: string) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  adminSetActive: (userId: string, active: boolean) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(userId)}/active`, {
      method: "POST",
      body: JSON.stringify({ active }),
    }),

  getLists: (since?: number) =>
    request<{ lists: GroceryList[] }>(`/api/lists${since !== undefined ? `?since=${since}` : ""}`),

  createList: (name: string) =>
    request<{ list: GroceryList }>("/api/lists", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  renameList: (id: string, name: string) =>
    request<{ list: GroceryList }>(`/api/lists/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  deleteList: (id: string) =>
    request<{ ok: true }>(`/api/lists/${encodeURIComponent(id)}`, { method: "DELETE" }),

  clearChecked: (listId: string) =>
    request<{ ok: true; cleared: number }>(
      `/api/lists/${encodeURIComponent(listId)}/clear-checked`,
      { method: "POST" },
    ),

  getItems: (listId: string, since?: number) =>
    request<{ items: GroceryItem[] }>(
      `/api/lists/${encodeURIComponent(listId)}/items${since !== undefined ? `?since=${since}` : ""}`,
    ),

  createItem: (
    listId: string,
    item: {
      id: string;
      name: string;
      quantity: number | null;
      unit: string | null;
      position: number;
      updatedAt: number;
    },
  ) =>
    request<{ item: GroceryItem }>(`/api/lists/${encodeURIComponent(listId)}/items`, {
      method: "POST",
      body: JSON.stringify(item),
    }),

  updateItem: (
    id: string,
    fields: Partial<{
      name: string;
      quantity: number | null;
      unit: string | null;
      checked: boolean;
      position: number;
      updatedAt: number;
    }>,
  ) =>
    request<{ item: GroceryItem; superseded?: boolean }>(`/api/items/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  deleteItem: (id: string) =>
    request<{ ok: true }>(`/api/items/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getItemSuggestions: () =>
    request<{ suggestions: { name: string; count: number }[] }>("/api/items/suggestions"),
};
